import { useState } from "react";
import { User, UserRound, Eye, Edit2, ShieldAlert, KeyRound, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useStaffAuth } from "../context/StaffAuthContext"; //[cite: 29]

// Owner APIs[cite: 29]
import {
  updateStaff,
  toggleStaffStatus,
  resetStaffPassword,
} from "../api/staffApi"; //[cite: 29]

// Portal APIs[cite: 29]
import {
  updateStaffForPortal,
  toggleStaffStatusForPortal,
  resetStaffPasswordForPortal,
} from "../api/staffPortalApi"; //[cite: 29]

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  Badge,
  Button,
  IconButton,
  Modal,
  Input,
  Select,
  Alert,
  EmptyState,
} from "../components/ui";

const EMPLOYMENT_TYPE_OPTIONS = [
  { value: "", label: "Select Type" },
  { value: "Full-time", label: "Full-time" },
  { value: "Part-time", label: "Part-time" },
  { value: "Contract", label: "Contract" },
];

const EMPLOYMENT_STATUS_OPTIONS = [
  { value: "", label: "Select Status" },
  { value: "Active", label: "Active" },
  { value: "Resigned", label: "Resigned" },
  { value: "Terminated", label: "Terminated" },
  { value: "On-leave", label: "On-leave" },
];

const StaffTable = ({ staff = [], refreshStaff, isPortal = false }) => {
  const navigate = useNavigate();
  const { hasPermission } = useStaffAuth();

  const canManage = isPortal
    ? hasPermission("STAFF_MANAGEMENT_FULL")
    : true; //[cite: 29]

  // Edit modal state[cite: 29]
  const [editStaff, setEditStaff] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  // Reset password result state[cite: 29]
  const [resetCredentials, setResetCredentials] = useState(null);
  const [resetLoading, setResetLoading] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(null);

  /* ── VIEW DETAIL ── [cite: 29]*/

  const handleView = (id) => {
    if (isPortal) {
      navigate(`/staff-panel/staff/${id}`); //[cite: 29]
    } else {
      navigate(`/staff/${id}`); //[cite: 29]
    }
  };

  /* ── EDIT ── [cite: 29]*/

  const openEdit = (item) => {
    setEditStaff(item);
    setEditForm({
      name: item.name,
      mobile: item.mobile,
      address: item.address || "",
      designation: item.designation || "",
      employmentType: item.employmentType || "",
      employmentStatus: item.employmentStatus || "",
      joiningDate: item.joiningDate
        ? new Date(item.joiningDate).toISOString().split("T")[0]
        : "",
    });
    setEditError("");
  };

  const closeEdit = () => {
    setEditStaff(null);
    setEditForm({});
    setEditError("");
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setEditLoading(true);
      setEditError("");

      if (isPortal) {
        await updateStaffForPortal(editStaff._id, editForm); //[cite: 29]
      } else {
        await updateStaff(editStaff._id, editForm); //[cite: 29]
      }

      await refreshStaff();
      closeEdit();
    } catch (err) {
      setEditError(err?.response?.data?.message || "Failed to update staff"); //[cite: 29]
    } finally {
      setEditLoading(false);
    }
  };

  /* ── TOGGLE STATUS ── [cite: 29]*/

  const handleToggleStatus = async (id) => {
    try {
      setToggleLoading(id);

      if (isPortal) {
        await toggleStaffStatusForPortal(id); //[cite: 29]
      } else {
        await toggleStaffStatus(id); //[cite: 29]
      }

      await refreshStaff();
    } catch (err) {
      console.error(err);
    } finally {
      setToggleLoading(null);
    }
  };

  /* ── RESET PASSWORD ── [cite: 29]*/

  const handleResetPassword = async (id) => {
    try {
      setResetCredentials(null);
      setResetLoading(id);

      const data = isPortal
        ? await resetStaffPasswordForPortal(id)
        : await resetStaffPassword(id); //[cite: 29]

      setResetCredentials(data.credentials); //[cite: 29]
    } catch (err) {
      console.error(err);
    } finally {
      setResetLoading(null);
    }
  };

  const columns = [
    {
      key: "name",
      header: "Staff Member",
      render: (item) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300">
            {item.gender === "Female" ? (
              <UserRound size={18} />
            ) : (
              <User size={18} />
            )}
          </div>
          <div>
            <span className="block font-semibold text-neutral-900 dark:text-neutral-100 text-xs sm:text-sm">
              {item.name}
            </span>
            {item.designation && (
              <span className="text-[11px] text-neutral-500 dark:text-neutral-400">{item.designation}</span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "role",
      header: "Role",
      render: (item) => (
        <Badge variant="primary" className="text-[11px]">
          {item?.roleId?.roleName || "Unassigned"}
        </Badge>
      ),
    },
    {
      key: "email",
      header: "Email",
      render: (item) => (
        <span className="text-xs text-neutral-600 dark:text-neutral-300">{item.email}</span>
      ),
    },
    {
      key: "mobile",
      header: "Mobile",
      nowrap: true,
      render: (item) => (
        <span className="font-mono text-xs text-neutral-800 dark:text-neutral-100">+{item.mobile}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => (
        <Badge variant={item.active ? "success" : "danger"}>
          {item.active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      nowrap: true,
      render: (item) => (
        <div className="flex items-center justify-end gap-1">
          {/* View Detail */}
          <IconButton
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleView(item._id)}
            title="View Details"
          >
            <Eye className="h-4 w-4 text-neutral-600 dark:text-neutral-300" />
          </IconButton>

          {canManage && (
            <>
              {/* Edit */}
              <IconButton
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => openEdit(item)}
                title="Edit Staff"
              >
                <Edit2 className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              </IconButton>

              {/* Toggle Status */}
              <IconButton
                type="button"
                variant="ghost"
                size="sm"
                isLoading={toggleLoading === item._id}
                onClick={() => handleToggleStatus(item._id)}
                title={item.active ? "Deactivate Staff" : "Activate Staff"}
              >
                <ShieldAlert
                  className={`h-4 w-4 ${
                    item.active ? "text-danger-600" : "text-success-600"
                  }`}
                />
              </IconButton>

              {/* Reset Password */}
              <IconButton
                type="button"
                variant="ghost"
                size="sm"
                isLoading={resetLoading === item._id}
                onClick={() => handleResetPassword(item._id)}
                title="Reset Password"
              >
                <KeyRound className="h-4 w-4 text-amber-600" />
              </IconButton>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Reset password result banner */}
      {resetCredentials && (
        <Alert
          variant="success"
          title="Password Reset Successfully"
          onClose={() => setResetCredentials(null)}
        >
          <div className="mt-1 space-y-0.5 text-xs font-mono">
            <p>
              Staff Code: <strong>{resetCredentials.staffCode}</strong>
            </p>
            <p>
              New Password: <strong>{resetCredentials.password}</strong>
            </p>
          </div>
        </Alert>
      )}

      {/* Table Card */}
      <Card>
        <CardHeader className="border-b bg-neutral-50/50">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-neutral-700 dark:text-neutral-200 shrink-0" />
              <span>Staff Directory</span>
            </CardTitle>
            <Badge variant="secondary" className="text-[11px]">
              Total: {staff.length}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <Table
            columns={columns}
            data={staff}
            emptyState={
              <EmptyState
                icon={Users}
                title="No staff members found"
                description="Staff accounts created will appear here in the directory."
              />
            }
          />
        </CardContent>
      </Card>

      {/* Edit Staff Modal */}
      {editStaff && (
        <Modal
          isOpen={!!editStaff}
          onClose={closeEdit}
          title="Edit Staff Member"
          className="w-full max-w-lg lg:max-w-xl"
          footer={
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={closeEdit}
                disabled={editLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="edit-staff-form"
                variant="primary"
                isLoading={editLoading}
              >
                {editLoading ? "Saving..." : "Save Changes"}
              </Button>
            </>
          }
        >
          <div className="max-h-[65vh] overflow-y-auto overflow-x-hidden space-y-4 pr-1 pt-1">
            {editError && (
              <Alert variant="danger" title="Error">
                {editError}
              </Alert>
            )}

            <form
              id="edit-staff-form"
              onSubmit={handleEditSubmit}
              className="space-y-4"
            >
              {/* Basic Info */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">
                  Basic Info
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Input
                    label="Full Name"
                    type="text"
                    value={editForm.name || ""}
                    onChange={(e) =>
                      setEditForm({ ...editForm, name: e.target.value })
                    }
                    required
                  />

                  <Input
                    label="Mobile Number"
                    type="text"
                    value={editForm.mobile || ""}
                    onChange={(e) =>
                      setEditForm({ ...editForm, mobile: e.target.value })
                    }
                    required
                  />

                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-neutral-700 dark:text-neutral-200 mb-1.5">
                      Address
                    </label>
                    <textarea
                      value={editForm.address || ""}
                      onChange={(e) =>
                        setEditForm({ ...editForm, address: e.target.value })
                      }
                      rows={2}
                      className="w-full rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-2.5 text-xs text-neutral-900 dark:text-neutral-100 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>
              </div>

              {/* Employment Info */}
              <div className="border-t border-neutral-200/60 pt-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">
                  Employment Info
                </p>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Input
                    label="Designation"
                    type="text"
                    placeholder="e.g. Class Teacher"
                    value={editForm.designation || ""}
                    onChange={(e) =>
                      setEditForm({ ...editForm, designation: e.target.value })
                    }
                  />

                  <Select
                    label="Employment Type"
                    value={editForm.employmentType || ""}
                    onChange={(val) =>
                      setEditForm({ ...editForm, employmentType: val })
                    }
                    options={EMPLOYMENT_TYPE_OPTIONS}
                  />

                  <Select
                    label="Employment Status"
                    value={editForm.employmentStatus || ""}
                    onChange={(val) =>
                      setEditForm({ ...editForm, employmentStatus: val })
                    }
                    options={EMPLOYMENT_STATUS_OPTIONS}
                  />

                  <Input
                    label="Joining Date"
                    type="date"
                    value={editForm.joiningDate || ""}
                    onChange={(e) =>
                      setEditForm({ ...editForm, joiningDate: e.target.value })
                    }
                  />
                </div>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default StaffTable;